import { useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate, Navigate, matchPath } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  Hidden,
  List,
  ListSubheader,
  Typography,
  Button,
  ListItem,
  IconButton,
  MenuItem,
  useMediaQuery
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/styles';
import { grey } from '@material-ui/core/colors';
import { useSelector, useDispatch } from 'react-redux';
import GroupsIcon from '@material-ui/icons/Groups';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import ReceiptIcon from '@material-ui/icons/Receipt';
import BuildIcon from '@material-ui/icons/Build';
import PieChartIcon from '@material-ui/icons/PieChart';
import TimelineIcon from '@material-ui/icons/Timeline';
import PersonIcon from '@material-ui/icons/Person';
import InboxIcon from '@material-ui/icons/Inbox';
import TagIcon from '@material-ui/icons/Tag';
import ImageIcon from '@material-ui/icons/Image';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import LinkIcon from '@material-ui/icons/Link';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import PeopleIcon from '@material-ui/icons/People';
import AccountBoxIcon from '@material-ui/icons/AccountBox';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import { setParam } from '../store/ducks/param';
import NavItem from './NavItem';

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0
  }
}));

const items = [
  {
    href: '/app/dashboard',
    icon: <PieChartIcon style={{ color: grey[700], fontSize: 20, marginRight: 10 }} />,
    title: 'Dashboard',
    nivel: 7,
    tipo: 'dashboard'
  },
  // Sistema de Afiliados
  {
    href: '/app/perfil-afiliado',
    icon: <AccountBoxIcon style={{ color: grey[700], fontSize: 20, marginRight: 10 }} />,
    title: 'Meu Perfil de Afiliado',
    nivel: 7,
    tipo: 'afiliados',
    perfil: 'all' // Visível para todos
  },
  {
    href: '/app/afiliados',
    icon: <PeopleIcon style={{ color: grey[700], fontSize: 20, marginRight: 10 }} />,
    title: 'Canais de Afiliados',
    nivel: 7,
    tipo: 'afiliados',
    perfil: 'all' // Visível para todos
  },
  {
    href: '/app/links',
    icon: <LinkIcon style={{ color: grey[700], fontSize: 20, marginRight: 10 }} />,
    title: 'Links de Afiliado',
    nivel: 7,
    tipo: 'afiliados',
    perfil: 'all'
  },
  {
    href: '/app/vendas',
    icon: <ShoppingCartIcon style={{ color: grey[700], fontSize: 20, marginRight: 10 }} />,
    title: 'Vendas',
    nivel: 7,
    tipo: 'afiliados',
    perfil: 'all'
  },
  {
    href: '/app/comissoes',
    icon: <AttachMoneyIcon style={{ color: grey[700], fontSize: 20, marginRight: 10 }} />,
    title: 'Comissões',
    nivel: 7,
    tipo: 'afiliados',
    perfil: 'all'
  },
  // Admin
  {
    href: '/app/admin/perfis-afiliados',
    icon: <VerifiedUserIcon style={{ color: grey[700], fontSize: 20, marginRight: 10 }} />,
    title: 'Perfis de Afiliados',
    nivel: 7,
    tipo: 'admin',
    perfil: 'admin' // Apenas admin
  },
  {
    href: '/app/usuarios',
    icon: <PersonIcon style={{ color: grey[700], fontSize: 20, marginRight: 10 }} />,
    title: 'Usuários',
    nivel: 7,
    tipo: 'admin',
    perfil: 'admin' // Apenas admin
  }
];

const DashboardSidebar = () => {
  const classes = useStyles();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const param = useSelector((state) => state.param);

  // Abaixo de lg o menu vira gaveta temporária (abre/fecha pelo hambúrguer e
  // fecha ao tocar fora). Acima de lg mantém o comportamento fixo de antes.
  const isMobile = useMediaQuery((t) => t.breakpoints.down('lg'));

  // sidebarOpenned é persistido pelo redux-persist e o padrão é true — sem isto
  // o app abriria com o menu por cima do conteúdo em toda carga no celular.
  // Depende só de isMobile de propósito: reagir a param reabriria o efeito a
  // cada toque no menu.
  useEffect(() => {
    if (isMobile && param.sidebarOpenned) {
      dispatch(setParam({ ...param, sidebarOpenned: false }));
    }
  }, [isMobile]);

  // Verifica se é admin pelo perfil OU pelo user.is_admin
  const isAdmin = auth.perfil === 'admin' || auth.user?.is_admin === true;

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          p: 2
        }}
      >
        <Avatar
          component={RouterLink}
          src="/public/static/avatar.png"
          sx={{
            cursor: 'pointer',
            width: 64,
            height: 64
          }}
          to="/app/settings"
        />
        <Typography color="textPrimary" variant="h5" sx={{ textAlign: 'center', my: 1 }}>
          {auth.user.name || auth.user.APELIDO}
        </Typography>
        <Typography color="textSecondary" variant="body2" fontSize={12}>
          {auth.user.email || `Código: ${auth.user.id}`}
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
        {items.filter((row) => row.tipo === 'dashboard').length > 0 && (
          <>
            <List>
              <ListSubheader sx={{ fontSize: 10 }}>Dashboard</ListSubheader>
              {items
                .filter((row) => row.tipo === 'dashboard')
                .map((item) => (
                  <MenuItem
                    key={item.title}
                    onClick={(event) => {
                      dispatch(setParam({ ...param, sidebarOpenned: false }));
                      window.location.href = item.href;
                    }}
                  >
                    {item.icon}
                    <Typography variant="title">{item.title}</Typography>
                  </MenuItem>
                ))}
            </List>
          </>
        )}
        {items.filter((row) => row.tipo === 'afiliados').length > 0 && (
          <>
            <Divider />
            <List>
              <ListSubheader sx={{ fontSize: 10 }}>Sistema de Afiliados</ListSubheader>
              {items
                .filter((row) => row.tipo === 'afiliados')
                .map((item) => (
                  <MenuItem
                    key={item.title}
                    onClick={(event) => {
                      dispatch(setParam({ ...param, sidebarOpenned: false }));
                      window.location.href = item.href;
                    }}
                  >
                    {item.icon}
                    <Typography variant="title">{item.title}</Typography>
                  </MenuItem>
                ))}
            </List>
          </>
        )}
        {isAdmin && items.filter((row) => row.tipo === 'parceiro').length > 0 && (
          <>
            <Divider />
            <List>
              <ListSubheader sx={{ fontSize: 10 }}>Clientes</ListSubheader>
              {items
                .filter((row) => row.tipo === 'parceiro')
                .map((item) => (
                  <MenuItem
                    key={item.title}
                    onClick={(event) => {
                      dispatch(setParam({ ...param, sidebarOpenned: false }));
                      window.location.href = item.href;
                    }}
                  >
                    {item.icon}
                    <Typography variant="title">{item.title}</Typography>
                  </MenuItem>
                ))}
            </List>
          </>
        )}
        {isAdmin && items.filter((row) => row.tipo === 'propaganda').length > 0 && (
          <>
            <Divider />
            <List>
              <ListSubheader sx={{ fontSize: 10 }}>Propagandas</ListSubheader>
              {items
                .filter((row) => row.tipo === 'propaganda')
                .map((item) => (
                  <MenuItem
                    key={item.title}
                    onClick={(event) => {
                      dispatch(setParam({ ...param, sidebarOpenned: false }));
                      window.location.href = item.href;
                    }}
                  >
                    {item.icon}
                    <Typography variant="title">{item.title}</Typography>
                  </MenuItem>
                ))}
            </List>
          </>
        )}
        {isAdmin && items.filter((row) => row.tipo === 'parceiro-sistema').length > 0 && (
          <>
            <Divider />
            <List>
              <ListSubheader sx={{ fontSize: 10 }}>Sistemas Parceiros</ListSubheader>
              {items
                .filter((row) => row.tipo === 'parceiro-sistema')
                .map((item) => (
                  <MenuItem
                    key={item.title}
                    onClick={(event) => {
                      dispatch(setParam({ ...param, sidebarOpenned: false }));
                      window.location.href = item.href;
                    }}
                  >
                    {item.icon}
                    <Typography variant="title">{item.title}</Typography>
                  </MenuItem>
                ))}
            </List>
          </>
        )}
        {isAdmin && items.filter((row) => row.tipo === 'contato').length > 0 && (
          <>
            <Divider />
            <List>
              <ListSubheader sx={{ fontSize: 10 }}>Contato</ListSubheader>
              {items
                .filter((row) => row.tipo === 'contato')
                .map((item) => (
                  <MenuItem
                    key={item.title}
                    onClick={(event) => {
                      dispatch(setParam({ ...param, sidebarOpenned: false }));
                      window.location.href = item.href;
                    }}
                  >
                    {item.icon}
                    <Typography variant="title">{item.title}</Typography>
                  </MenuItem>
                ))}
            </List>
          </>
        )}
        {isAdmin && items.filter((row) => row.tipo === 'admin').length > 0 && (
          <>
            <Divider />
            <List>
              <ListSubheader sx={{ fontSize: 10 }}>Administração</ListSubheader>
              {items
                .filter((row) => row.tipo === 'admin')
                .map((item) => (
                  <MenuItem
                    key={item.title}
                    onClick={(event) => {
                      dispatch(setParam({ ...param, sidebarOpenned: false }));
                      window.location.href = item.href;
                    }}
                  >
                    {item.icon}
                    <Typography variant="title">{item.title}</Typography>
                  </MenuItem>
                ))}
            </List>
          </>
        )}
        <Divider />
        <List>
          {items
            .filter((row) => row.tipo === 'geral')
            .map((item) => (
              <MenuItem
                key={item.title}
                onClick={(event) => {
                  dispatch(setParam({ ...param, sidebarOpenned: false }));
                  window.location.href = item.href;
                }}
              >
                {item.icon}
                <Typography variant="title">{item.title}</Typography>
              </MenuItem>
              // <NavItem href={item.href} key={item.title} title={item.title} icon={item.icon} onClick={() => dispatch(setParam({ ...param, sidebarOpenned: false }))} />
            ))}
        </List>
      </Box>
      <Box sx={{ flexGrow: 1 }} />
    </Box>
  );

  // No desktop, páginas sem menu lateral fixo (dashboard, performace, contato)
  // usam gaveta retrátil; as demais mantêm o menu sempre visível.
  const variantDesktop =
    window.location.href.indexOf('dashboard') <= -1 &&
    window.location.href.indexOf('performace') <= -1 &&
    window.location.href.indexOf('contato') <= -1
      ? 'permanent'
      : 'persistent';

  return (
    <>
      <Drawer
        anchor="left"
        onClose={() => dispatch(setParam({ ...param, sidebarOpenned: false }))}
        open={param.sidebarOpenned}
        variant={isMobile ? 'temporary' : variantDesktop}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          elevation: 16,
          sx: {
            width: 256,
            // No celular a gaveta ocupa a tela inteira sobre a AppBar; no
            // desktop continua abaixo dela (marginTop 8 = 64px).
            marginTop: isMobile ? 0 : 8,
            overflowY: 'auto'
          }
        }}
      >
        {content}
      </Drawer>
    </>
  );
};

DashboardSidebar.defaultProps = {
  onMobileClose: () => {},
  openMobile: false
};

export default DashboardSidebar;
